import React, { useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import css from "@styled-system/css";
import { LayoutProps, SpaceProps, layout, space } from "styled-system";

import Button from "../Button/Button";
import Box from "../Box/Box";
import BCAFooterMenu from "./BCAFooterMenu";
import LegalInfo from "./LegalInfo";
import SocialMedia from "./SocialMedia";

export interface BCAFooterProps {
	/**
  - urls object has the following props
  - navigationLinks: string; // Url to get the footer links split in sections
  - legalInfo: string; // Url to get the legal info data
  - socialMedia: string; //Url to get twitter and linkedIn links
   */
	urls: {
		navigationLinks: string;
		legalInfo: string;
		socialMedia: string;
	};
}

export interface LinkProps {
	id: string;
	link: string;
	isVisible: boolean;
	label: string;
	openInNewWindow: boolean;
}

const Footer = styled.div<LayoutProps & SpaceProps>`
	width: 100%;
	display: flex;
	flex-direction: column;
	${css({
		fontFamily: "normal",
	})};
	${layout}
	${space}
`;

const StaticSection = styled.div`
	display: grid;
	overflow: hidden;
	width: 100%;
	height: 25%;
	@media (min-width: 640px) {
		grid-template-columns: 4fr 1fr;
	}
`;

const ContactInfo = styled.div`
	${css({
		fontSize: "caption",
		lineHeight: [1.5, 1.6],
		opacity: 0.6,
		color: "primary-white",
		maxWidth: "620px",
		textAlign: ["center", "left"],
	})};
`;

const BackToTopButton = () => {
	const themeContext = useContext(ThemeContext);
	const scrollToTop = () =>
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});

	return (
		<Button
			text="Back to top"
			variant="tertiary"
			onClick={scrollToTop}
			css={`
				background-color: ${themeContext.colors["secondary-darkest-grey"]};
				color: ${themeContext.colors["primary-white"]};
				font-size: ${themeContext.fontSizes["caption"]}px;
				border-radius: 0;
				min-height: 50px;
				margin: 0;
				cursor: pointer;
				&:not([disabled]):hover {
					color: ${themeContext.colors["primary-light-blue"]};
				}
			`}
		></Button>
	);
};

const ContactInfoSection = () => (
	<ContactInfo data-testid="contact-info-section">
		British Car Auctions Limited, registered in England and Wales No. 00438886
		<br />
		Registered office: Form 2, 18 Bartley Wood Business Park, Bartley Way, Hook,
		Hampshire, RG27 9XA, United Kingdom
		<br />
		VAT No. GB 188155238.
	</ContactInfo>
);

export default function BCAFooter({ urls }: BCAFooterProps) {
	return (
		<Footer data-testid="footer-container">
			<BackToTopButton />
			<Box
				data-testid="content-section"
				display="flex"
				flexDirection="column"
				alignItems="center"
				backgroundColor="footer-blue"
			>
				<Box
					width="100%"
					maxWidth="maxGridWidth"
					px={{ _: "mobileGridMargin", desktop: "desktopGridMargin" }}
					py={8}
					display="flex"
					flexDirection="column"
					justifyContent="space-between"
				>
					<BCAFooterMenu
						navigationLinksUrl={urls.navigationLinks}
					></BCAFooterMenu>
					<StaticSection>
						<Box
							display="flex"
							flexDirection="column"
							alignItems={["center", "flex-start"]}
							mb={[5, 0]}
						>
							<LegalInfo legalInfoUrl={urls.legalInfo} />
							<ContactInfoSection />
						</Box>
						<SocialMedia socialMediaUrl={urls.socialMedia} />
					</StaticSection>
				</Box>
			</Box>
		</Footer>
	);
}
